import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { TasksPriorities, TasksStatuses } from "src/modules/3rd-party-management/constants";

import styles from "../../Styles";
import {
    buildDueDiligenceRows,
    buildProductDetailRows,
    buildProductSummaryRows,
} from "../../helpers/productExport";
import {
    normalizeMetadataReports,
    normalizeQuestionnaireReports,
} from "../../helpers/reportExport";
import { formatDisplayValue, stripHtmlToText } from "../../helpers";
import { getRiskTitleByColor } from "../../helpers/managementSummary";
import { KeyValueTable, SectionTitle, SubSectionTitle } from "../shared";
import { TOC_ANCHORS } from "../../helpers/tableOfContents";
import Reports from "../../Sections/Reports";

const formatTimestamp = (dateUtils, timestamp) => {
    if (!timestamp) {
        return "-";
    }
    return dateUtils.convertTimeStampToDate(timestamp, INTERNATIONAL_DATE_FORMAT);
};

const OperationsAndRiskSections = ({ t, supplierData = {} }) => {
    const dateUtils = new DateUtils();
    const products = Array.isArray(supplierData?.products) ? supplierData.products : [];
    const supplierCountry = supplierData?.supplierDetail?.country;
    const findings = Array.isArray(supplierData?.news) ? supplierData.news : [];
    const risks = Array.isArray(supplierData?.risks) ? supplierData.risks : [];
    const tasks = Array.isArray(supplierData?.tasks) ? supplierData.tasks : [];
    const reports = Array.isArray(supplierData?.reports) ? supplierData.reports : [];
    const relatedIncidents = Array.isArray(supplierData?.relatedIncidents) ? supplierData.relatedIncidents : [];
    const questionnaireReports = normalizeQuestionnaireReports(reports);
    const metadataReports = normalizeMetadataReports(reports);

    const productRows = buildProductSummaryRows(products);
    const productDetailRows = buildProductDetailRows(products, {
        dateUtils,
        t,
        supplierCountry,
    }).map((row) => ({
        ...row,
        details: formatDisplayValue(row.details),
    }));
    const dueDiligenceRows = buildDueDiligenceRows(products, {
        supplierCountry,
    }).map((row) => ({
        ...row,
        details: formatDisplayValue(row.details),
    }));

    const findingRows = findings.map((finding, index) => ({
        title: `${index + 1}. ${finding?.news_agency || "-"}`,
        details: finding?.title || finding?.keyword || "-",
    }));

    const incidentRows = relatedIncidents.map((incident, index) => ({
        title: `${incident?.type || "Incident"} ${index + 1}`,
        details: [
            `Secondary ID: ${incident?.secondaryId || "-"}`,
            `Status: ${incident?.status || "-"}`,
            `Submission Date: ${incident?.submissionDate || "-"}`,
        ].join(" | "),
    }));

    const reportSummaryRows = [
        { title: "Open Requests", details: metadataReports.filter((report) => report?.status === "OPEN").length || 0 },
        { title: "Reports Submitted", details: metadataReports.filter((report) => report?.status === "SUBMITTED").length || 0 },
        { title: "Total Number of Reports", details: metadataReports.length || 0 },
    ];

    const reportRows = metadataReports.map((report, index) => ({
        title: `${index + 1}. ${report?.title || "External Company Report"}`,
        details: [
            `Request Date: ${report?.requestDate || "-"}`,
            `Report Type: ${report?.type || report?.reportType || "-"}`,
            `Submission Date: ${report?.submissionDate || "-"}`,
            `Status: ${report?.status || "-"}`,
        ].join(" | "),
    }));

    const riskRows = risks.map((risk) => ({
        title: `R${risk?.secondary_id || "-"}`,
        details: [
            `Title: ${stripHtmlToText(risk?.description, "-")}`,
            `Category: ${risk?.category?.name || "-"}`,
            `Residual Risk Rate: ${risk?.result_color ? getRiskTitleByColor(risk.result_color) : "-"}`,
            `Risk Owner: ${risk?.owner_name || "-"}`,
            `Status: ${risk?.is_deleted ? "Deleted" : "Active"}`,
        ].join(" | "),
    }));

    const taskRows = tasks.map((task) => ({
        title: `T${task?.secondary_id || "-"}`,
        details: [
            `Title: ${task?.name || "-"}`,
            `Creation Date: ${formatTimestamp(dateUtils, task?.created_at)}`,
            `Deadline: ${formatTimestamp(dateUtils, task?.ended_at)}`,
            `Task Manager: ${task?.manager || "-"}`,
            `Task Owner: ${task?.analyst?.full_name || "-"}`,
            `Priority: ${TasksPriorities[task?.priority]?.title || "-"}`,
            `Status: ${TasksStatuses[task?.status]?.title || task?.status || "-"}`,
        ].join(" | "),
    }));

    return (
        <View style={styles.wordSectionContent}>
            <SectionTitle title="Products" number="6" anchorId={TOC_ANCHORS.SECTION_6} />
            <KeyValueTable rows={productRows} titleColumnLabel="ID" detailsColumnLabel="Product Name / Details" />

            <SubSectionTitle title="Products Detail" number="6.1." anchorId={TOC_ANCHORS.SECTION_6_1} />
            <KeyValueTable rows={productDetailRows} titleColumnLabel="ID" detailsColumnLabel="Details" />

            <SubSectionTitle title="Deforestation Due Diligence" number="6.2." anchorId={TOC_ANCHORS.SECTION_6_2} />
            <KeyValueTable rows={dueDiligenceRows} titleColumnLabel="ID" detailsColumnLabel="Details" />

            <SectionTitle title="Findings" number="7" anchorId={TOC_ANCHORS.SECTION_7} />
            <KeyValueTable rows={findingRows} titleColumnLabel="Source" detailsColumnLabel="Description" />

            <SectionTitle title="Related Incidents" number="8" anchorId={TOC_ANCHORS.SECTION_8} />
            <KeyValueTable rows={incidentRows} titleColumnLabel="Type" detailsColumnLabel="Details" />

            <SectionTitle title="Reports" number="9" anchorId={TOC_ANCHORS.SECTION_9} />
            {questionnaireReports.length > 0 ? (
                <Reports reports={questionnaireReports} embedded />
            ) : metadataReports.length > 0 ? (
                <>
                    <KeyValueTable rows={reportSummaryRows} titleColumnLabel="Metric" detailsColumnLabel="Value" />
                    <KeyValueTable rows={reportRows} titleColumnLabel="Report" detailsColumnLabel="Details" />
                </>
            ) : (
                <KeyValueTable
                    rows={[{ title: "No Data", details: "-" }]}
                    titleColumnLabel="Report"
                    detailsColumnLabel="Details"
                />
            )}

            <SectionTitle title="Risk Assessment" number="10" anchorId={TOC_ANCHORS.SECTION_10} />
            <KeyValueTable rows={riskRows} titleColumnLabel="Risk ID" detailsColumnLabel="Details" />

            <SectionTitle title="Task Management" number="11" anchorId={TOC_ANCHORS.SECTION_11} />
            <KeyValueTable rows={taskRows} titleColumnLabel="Task ID" detailsColumnLabel="Details" />

            {tasks.length > 0 && (
                <View style={localStyles.statusSummaryWrap}>
                    <Text style={localStyles.statusSummaryText}>
                        {`${t("New")}: ${tasks.filter((task) => task?.status === "NEW").length} | `}
                        {`${t("Review")}: ${tasks.filter((task) => task?.status === "REVIEW").length} | `}
                        {`${t("In Progress")}: ${tasks.filter((task) => task?.status === "IN_PROGRESS").length}`}
                    </Text>
                </View>
            )}
        </View>
    );
};

const localStyles = {
    statusSummaryWrap: {
        paddingHorizontal: 30,
        marginTop: 6,
        marginBottom: 10,
    },
    statusSummaryText: {
        fontSize: 8,
        color: "#1F2937",
    },
};

export default withNamespaces()(OperationsAndRiskSections);
